// components/Header.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { logoutUser } from '../services/api';
import { MapPin, Phone, Menu, X, User, LogOut, LayoutDashboard, Bell, ChevronDown, ShoppingCart } from 'lucide-react';
import AuthModal from './AuthModal';
import Cart from './Cart';
import { useCart } from '../context/CartContext';

const UserProfile = ({ user, onLogout, navigate }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="user-profile">
      <div 
        className="user-trigger"
        onClick={() => setShowDropdown(!showDropdown)}
      >
        <div className="user-avatar">
          <User size={20} />
        </div>
        <span className="desktop-only user-name">
          Hi, {user?.displayName?.split(' ')[0] || user?.email?.split('@')[0] || 'User'}
        </span>
        <ChevronDown size={16} className={`dropdown-arrow ${showDropdown ? 'rotated' : ''}`} />
      </div>
      
      {showDropdown && (
        <div className="user-dropdown">
          <div className="user-info">
            <strong>{user?.displayName || user?.email || 'User'}</strong>
            <span>{user?.email}</span>
          </div>
          <div className="dropdown-divider"></div>
          <button 
            onClick={() => {
              navigate('/dashboard');
              setShowDropdown(false);
            }} 
            className="dropdown-item"
          >
            <LayoutDashboard size={16} />
            Dashboard
          </button>
          <button 
            onClick={() => {
              navigate('/dashboard?tab=profile');
              setShowDropdown(false);
            }} 
            className="dropdown-item"
          >
            <User size={16} />
            Profile Settings
          </button>
          <div className="dropdown-divider"></div>
          <button onClick={onLogout} className="dropdown-item logout">
            <LogOut size={16} />
            Logout
          </button>
        </div>
      )}
    </div>
  );
};

const Header = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getCartItemCount } = useCart();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authType, setAuthType] = useState('login');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showCart, setShowCart] = useState(false);

  const cartItemCount = getCartItemCount();

  // Handle scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const handleLogin = () => {
    setAuthType('login');
    setShowAuthModal(true);
    setMobileMenuOpen(false);
  };

  const handleSignup = () => {
    setAuthType('signup');
    setShowAuthModal(true);
    setMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logoutUser();
      onLogout?.();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      onLogout?.();
      navigate('/');
    }
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    closeMobileMenu();
  };

  return (
    <>
      {/* Top Bar */}
      <div className="top-bar">
        <div className="container">
          <div className="top-bar-content">
            <div className="location">
              <MapPin size={14} />
              <span>Service available across India</span>
            </div>
            <div className="contact-info">
              <Phone size={14} />
              <span>Call: +91 98765 43210</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className={`header ${scrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="nav-container">
            <div className="logo" onClick={() => navigate('/home')} style={{ cursor: 'pointer' }}>
              <div className="logo-icon">
                <img src="/logo.png" alt="EaseMyHomeServices Logo" style={{ width: '40px', height: '40px', objectFit: 'contain' }} />
              </div>
              <div className="logo-text desktop-only">
                <h1>EaseMyHomeServices</h1>
              </div>
            </div>
            
            <nav className={`nav ${mobileMenuOpen ? 'nav-open' : ''}`}>
              <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }}>Services</a>
              <a href="#how-it-works" onClick={(e) => { e.preventDefault(); scrollToSection('how-it-works'); }}>How It Works</a>
              <a href="#why-choose-us" onClick={(e) => { e.preventDefault(); scrollToSection('why-choose-us'); }}>Why Choose Us</a>
              
              {/* Mobile Auth Buttons */}
              <div className="mobile-only auth-buttons-mobile">
                {/* Mobile Cart */}
                <button className="mobile-cart-button" onClick={() => {
                  setShowCart(true);
                  closeMobileMenu();
                }}>
                  <ShoppingCart size={16} />
                  Cart {cartItemCount > 0 && `(${cartItemCount})`}
                </button>

                {user ? (
                  <div className="user-info-mobile">
                    <button 
                      onClick={() => {
                        navigate('/dashboard');
                        closeMobileMenu();
                      }} 
                      className="btn-dashboard-mobile"
                    >
                      <LayoutDashboard size={16} />
                      Dashboard
                    </button>
                    <button onClick={handleLogout} className="btn-logout-mobile">
                      <LogOut size={16} />
                      Logout
                    </button>
                  </div>
                ) : (
                  <div className="auth-buttons-container">
                    <button className="btn-login" onClick={handleLogin}>
                      Login
                    </button>
                    <button className="btn-signup" onClick={handleSignup}>
                      Sign Up
                    </button>
                  </div>
                )}
              </div>
            </nav>

            {/* Desktop Auth Section */}
            <div className="auth-section desktop-only">
              {/* Cart Button */}
              <button className="cart-button" onClick={() => setShowCart(true)}>
                <ShoppingCart size={20} />
                {cartItemCount > 0 && (
                  <span className="cart-badge">{cartItemCount}</span>
                )}
              </button>

              {user ? (
                <UserProfile user={user} onLogout={handleLogout} navigate={navigate} />
              ) : (
                <div className="auth-buttons">
                  <button className="btn-login" onClick={handleLogin}>
                    Login
                  </button>
                  <button className="btn-signup" onClick={handleSignup}>
                    Sign Up
                  </button>
                </div>
              )}
            </div>

            {/* Mobile Actions */}
            <div className="mobile-actions mobile-only">
              {/* Mobile Cart Icon - Left */}
              <button 
                className="mobile-cart-icon"
                onClick={() => setShowCart(true)}
                aria-label="Open cart"
              >
                <ShoppingCart size={20} />
                {cartItemCount > 0 && (
                  <span className="cart-badge">{cartItemCount}</span>
                )}
              </button>

              {/* Hamburger Menu - Right */}
              <button 
                className="mobile-menu-btn"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="nav-overlay nav-open" 
          onClick={closeMobileMenu}
        />
      )}

      <AuthModal 
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        type={authType}
        onSwitchType={() => setAuthType(authType === 'login' ? 'signup' : 'login')}
        onLoginSuccess={() => window.location.reload()}
      />

      <Cart 
        isOpen={showCart} 
        onClose={() => setShowCart(false)} 
        user={user}
      />
    </>
  );
};

export default Header;